import React, { useState } from 'react';

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ImportModal: React.FC<ImportModalProps> = ({ isOpen, onClose }) => {
  const [selectedType, setSelectedType] = useState<'objets' | 'pieces' | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setUploadStatus({ type: null, message: '' });
    }
  };

  const handleDownloadTemplate = (type: 'objets' | 'pieces') => {
    let csvContent = '';
    let filename = '';

    if (type === 'objets') {
      csvContent = `nom,categorie,sousCategorie,quantite,etat,besoinAchat,remarques,zoneCode,boxCode
Ballon de football,Jeux extérieurs,Ballons & sports,2,bon,false,En bon état,ROB2-AR-A01,
Corde à sauter,Matériel créatif & manuel,Travaux manuels,5,bon,false,,ROB2-AR-A02,
Livre de contes,Bible & pédagogie spirituelle,Bibles,10,incomplet,true,Quelques livres abîmés,ROB2-AR-A03,`;
      filename = 'modele_objets.csv';
    } else {
      csvContent = `code,nom,batimentCode
ROB2-SALLE-PEDAGOGIQUE,Salle pédagogique,ROB2
ROB2-SALLE-POLYVALENTE,Salle polyvalente,ROB2
ROB2-CUISINE,Cuisine,ROB2
ROB1-ACCUEIL,Accueil,ROB1
FANNY-GARAGE,Garage,NICE-FANNY
ANDO-BUREAU,Bureau,NICE-ANDO`;
      filename = 'modele_pieces.csv';
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUpload = async () => {
    if (!file || !selectedType) return;

    setIsUploading(true);
    setUploadStatus({ type: null, message: '' });

    try {
      // TODO: Implémenter l'upload réel vers le backend
      // const formData = new FormData();
      // formData.append('file', file);
      // const response = await apiClient.post(`/import/${selectedType}`, formData);

      // Simulation pour l'instant
      await new Promise(resolve => setTimeout(resolve, 2000));

      setUploadStatus({
        type: 'success',
        message: `Import de ${selectedType} réussi ! Les données seront disponibles après rafraîchissement.`
      });
      setFile(null);
      setSelectedType(null);
    } catch (error) {
      setUploadStatus({
        type: 'error',
        message: 'Erreur lors de l\'import. Veuillez vérifier le format du fichier.'
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                📥 Import de données
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            {/* Status Message */}
            {uploadStatus.type && (
              <div className={`mb-4 p-3 rounded-md ${
                uploadStatus.type === 'success' 
                  ? 'bg-green-50 text-green-800 border border-green-200' 
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                <p className="text-sm">{uploadStatus.message}</p>
              </div>
            )}

            {/* Type Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de données à importer
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setSelectedType('objets')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    selectedType === 'objets'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-3xl mb-2">📦</div>
                  <div className="text-sm font-medium text-gray-900">Objets</div>
                  <div className="text-xs text-gray-500 mt-1">Inventaire</div>
                </button>
                <button
                  onClick={() => setSelectedType('pieces')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    selectedType === 'pieces'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-3xl mb-2">🏠</div>
                  <div className="text-sm font-medium text-gray-900">Pièces</div>
                  <div className="text-xs text-gray-500 mt-1">Emplacements</div>
                </button>
              </div>
            </div>

            {/* Download Template */}
            {selectedType && (
              <div className="mb-4">
                <button
                  onClick={() => handleDownloadTemplate(selectedType)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  <span>📄</span>
                  <span className="text-sm font-medium">Télécharger le modèle CSV</span>
                </button>
              </div>
            )}

            {/* File Upload */}
            {selectedType && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fichier CSV à importer
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100
                    cursor-pointer"
                />
                {file && (
                  <p className="mt-2 text-sm text-gray-600">
                    Fichier sélectionné : <span className="font-medium">{file.name}</span>
                  </p>
                )}
              </div>
            )}

            {/* Instructions */}
            {selectedType && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">
                  📋 Instructions
                </h4>
                <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                  {selectedType === 'objets' ? (
                    <>
                      <li>Téléchargez le modèle CSV et remplissez-le</li>
                      <li>Colonnes obligatoires : nom, categorie, quantite, etat</li>
                      <li>États possibles : bon, incomplet, neuf, usé, à réparer, hs</li>
                      <li>Utilisez les codes de zones (ex: ROB2-AR-A01) ou boxes existants</li>
                    </>
                  ) : (
                    <>
                      <li>Téléchargez le modèle CSV et remplissez-le</li>
                      <li>Colonnes obligatoires : code, nom, batimentCode</li>
                      <li>Codes bâtiments : ROB1, ROB2, NICE-FANNY, NICE-ANDO</li>
                      <li>Format code pièce : [BATIMENT]-[NOM] (ex: ROB2-CUISINE)</li>
                    </>
                  )}
                </ul>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={handleUpload}
              disabled={!file || !selectedType || isUploading}
              className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${
                !file || !selectedType || isUploading
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isUploading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Import en cours...
                </>
              ) : (
                'Importer'
              )}
            </button>
            <button
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportModal;

